import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Film, Tv, Gamepad2, Youtube, Radio, Mail, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

function Reveal({ children, delay = 0, direction = 'up' }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const dirs = { up: { y: 40 }, left: { x: -50 }, right: { x: 50 } };
  const d = dirs[direction] || dirs.up;
  return (
    <motion.div ref={ref}
      initial={{ opacity: 0, ...d }}
      animate={isInView ? { opacity: 1, y: 0, x: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >{children}</motion.div>
  );
}

export default function Licensing() {
  const useCases = [
    { icon: <Film size={28} />, title: 'Film & TV', desc: 'Score your next film, documentary, or streaming series with our ambient and lofi catalog.' },
    { icon: <Tv size={28} />, title: 'Advertising', desc: 'Set the perfect mood for commercial campaigns, brand videos, and promotional content.' },
    { icon: <Gamepad2 size={28} />, title: 'Video Games', desc: 'Immersive lofi soundscapes for indie games, mobile apps, and interactive experiences.' },
    { icon: <Youtube size={28} />, title: 'YouTube & Social', desc: 'Background music for YouTube videos, podcasts, TikTok, Instagram Reels, and more.' },
    { icon: <Radio size={28} />, title: 'Live Events', desc: 'Ambient music for cafes, galleries, retail spaces, and live event atmospheres.' },
  ];

  const tiers = [
    { name: 'Personal', price: 'Free', desc: 'Non-commercial, personal projects', features: ['Personal YouTube (no monetization)', 'Student projects', 'Credit required'] },
    { name: 'Creator', price: '$49', desc: 'Per track, single commercial use', features: ['Monetized YouTube/TikTok', 'Podcast episodes', 'Up to 100K views', 'Social media content'], highlighted: false },
    { name: 'Business', price: '$199', desc: 'Per track, broad commercial use', features: ['Film & TV productions', 'Advertising campaigns', 'Video games', 'Unlimited views', 'Global broadcast rights'], highlighted: true },
    { name: 'Enterprise', price: 'Custom', desc: 'Catalog access & custom scoring', features: ['Full catalog access', 'Custom compositions', 'Exclusive licensing', 'Priority support', 'Dedicated account manager'] },
  ];

  return (
    <div className="lic-page">
      <div className="lic-hero">
        <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          License Our <span>Music</span>
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
          Bring the CloudfiMusic sound to your projects. From indie films to global campaigns.
        </motion.p>
      </div>

      {/* Use cases */}
      <div className="lic-cases">
        {useCases.map((c, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="lic-case">
              <div className="lic-case-icon">{c.icon}</div>
              <h3>{c.title}</h3>
              <p>{c.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Pricing tiers */}
      <div className="lic-tiers-section">
        <Reveal><h2 className="lic-tiers-title">Licensing <span>Tiers</span></h2></Reveal>
        <div className="lic-tiers">
          {tiers.map((t, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className={`lic-tier ${t.highlighted ? 'lic-tier-hl' : ''}`}>
                {t.highlighted && <span className="lic-tier-badge">Most Popular</span>}
                <h3>{t.name}</h3>
                <div className="lic-tier-price">{t.price}<span>{t.price !== 'Free' && t.price !== 'Custom' ? '/track' : ''}</span></div>
                <p className="lic-tier-desc">{t.desc}</p>
                <ul>{t.features.map((f, j) => <li key={j}>{f}</li>)}</ul>
                <a href="mailto:info@cloudfimusic.com?subject=Licensing Inquiry - ${t.name}" className="lic-tier-btn">
                  {t.price === 'Custom' ? 'Contact Us' : 'Get License'} <ArrowRight size={14} />
                </a>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="lic-cta">
        <Reveal>
          <Mail size={32} />
          <h2>Custom Licensing?</h2>
          <p>Need something specific? Contact us for custom licensing arrangements.</p>
          <Link to="/contact" className="btn btn-primary">Get in Touch <ArrowRight size={16} /></Link>
        </Reveal>
      </div>
    </div>
  );
}
